import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Spinner from '../components/Spinner';
import Footer from '../components/Footer';
import { Helmet } from 'react-helmet';
import { useAuth } from '../context/AuthContext';
import { premiumQuizAPI, batchAPI } from '../services/api';
import '../styles/PremiumTests.css';
import EnglishVocabImg from '../assets/EnglishVocab.jpeg';

export default function PremiumTests() {
  const formatDate = (timestamp) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const date = new Date(timestamp);
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const HOST = process.env.REACT_APP_HOST_NAME;

  const [quizzes, setQuizzes] = useState([]);
  const [batch, setBatch] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [error, setError] = useState(null);
  const [deletingQuizId, setDeletingQuizId] = useState(null); // ✅ Track which quiz is being deleted

  // Check if user has access to this batch
  const checkAccess = async () => {
    if (!isAuthenticated) {
      setCheckingAccess(false);
      setHasAccess(false);
      return;
    }

    try {
      setCheckingAccess(true);
      const response = await batchAPI.checkBatchAccess(batchId);
      if (response.data.success) {
        setHasAccess(response.data.hasAccess || response.data.isAdmin);
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
    } finally {
      setCheckingAccess(false);
    }
  };

  // Fetch batch details
  const fetchBatchDetails = async () => {
    try {
      const response = await batchAPI.getBatchDetails(batchId);
      if (response.data.success) {
        setBatch(response.data.batch);
      }
    } catch (error) {
      console.error('Error fetching batch details:', error);
      setError('Failed to load batch details');
    }
  };

  // Fetch quizzes
  const fetchQuizzes = async (page = 1) => {
    if (!hasAccess && !checkingAccess) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await premiumQuizAPI.getBatchQuizzes(batchId, page, 15);
      if (response.data.success) {
        setQuizzes(response.data.quizzes);
        setTotalPages(response.data.totalPages);
        setCurrentPage(response.data.currentPage);
      }
    } catch (error) {
      console.error('Error fetching quizzes:', error);
      if (error.response?.data?.needsEnrollment) {
        setError('You need to enroll in this batch to access quizzes');
        setHasAccess(false);
      } else if (error.response?.data?.needsLogin) {
        setError('Please login to access this content');
      } else {
        setError('Failed to load quizzes');
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ Delete quiz function (Admin only)
  const handleDeleteQuiz = async (quizId, quizSubject, e) => {
    e.preventDefault(); // Prevent navigation to quiz
    e.stopPropagation(); // Stop event bubbling

    if (!window.confirm(`Are you sure you want to delete the quiz "${quizSubject}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingQuizId(quizId);
      
      const response = await fetch(`${HOST}api/delete-premium-quiz/${quizId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete quiz');
      }

      // Show success message
      alert(data.message || 'Quiz deleted successfully!');
      
      // Refresh the quizzes list
      fetchQuizzes(currentPage);
      
    } catch (error) {
      console.error('Error deleting quiz:', error);
      alert(error.message || 'Failed to delete quiz. Please try again.');
    } finally {
      setDeletingQuizId(null);
    }
  };

  useEffect(() => {
    fetchBatchDetails();
    checkAccess();
  }, [batchId, isAuthenticated]);

  useEffect(() => {
    if (!checkingAccess) {
      fetchQuizzes(currentPage);
    }
  }, [currentPage, hasAccess, checkingAccess]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleEnroll = () => {
    navigate(`/premium-batch/${batchId}`);
  };

  const handleLogin = () => {
    navigate('/login');
  };

  // Show loading while checking access
  if (checkingAccess || !batch) {
    return (
      <div className="quizes-page">
        <Navbar />
        <div className="loading-container">
          <Spinner />
          <p>Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  // Show enrollment required message
  if (!isAuthenticated) {
    return (
      <div className="quizes-page">
        <Helmet>
          <title>{batch.name} - Spardhaweb</title>
          <meta name="description" content={`Access ${batch.name} quizzes on Spardha Web`} />
        </Helmet>
        <Navbar />
        <div className="container my-5">
          <div className="access-denied-card">
            <div className="access-denied-icon">🔒</div>
            <h2>Login Required</h2>
            <p>Please login to access {batch.name} quizzes</p>
            <button onClick={handleLogin} className="btn btn-primary btn-lg">
              Login with Google
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show enrollment required for non-admin users without access
  if (!hasAccess && user?.role !== 'admin') {
    return (
      <div className="quizes-page">
        <Helmet>
          <title>{batch.name} - Spardhaweb</title>
          <meta name="description" content={`Access ${batch.name} quizzes on Spardha Web`} />
        </Helmet>
        <Navbar />
        <div className="container my-5">
          <div className="access-denied-card">
            <div className="access-denied-icon">⛔</div>
            <h2>Enrollment Required</h2>
            <p>You need to enroll in <strong>{batch.name}</strong> to access the quizzes</p>
            <div className="batch-info-preview">
              <img
                src={batch.imageUrl || EnglishVocabImg}
                alt={batch.name}
                className="batch-preview-img"
              />
              <h4>{batch.name}</h4>
              <p>📚 {batch.subject}</p>
              {batch.price > 0 && <p className="price">₹{batch.price}</p>}
            </div>
            <button onClick={handleEnroll} className="btn btn-success btn-lg">
              📱 Enroll Now
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="quizes-page">
      <Helmet>
        <title>{batch.name} - Spardhaweb</title>
        <meta name="description" content={`Access ${batch.name} quizzes on Spardha Web. Boost your preparation with comprehensive test series.`} />
        <meta name="keywords" content={`${batch.subject}, MPSC preparation, test series, ${batch.name}`} />
        <meta property="og:title" content={`${batch.name} - Spardha Web`} />
        <meta property="og:description" content={batch.description || `Access ${batch.name} quizzes`} />
        <meta property="og:image" content={batch.imageUrl || "%PUBLIC_URL%/logo_.jpg"} />
        <meta property="og:url" content={`https://spardhaweb.com/premium-tests/${batchId}`} />
      </Helmet>
      <Navbar />

      {/* Batch Header */}
      <div className="batch-header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-8">
              <h1 className="batch-title">{batch.name}</h1>
              <p className="batch-subject">📚 {batch.subject}</p>
              {user?.role === 'admin' && (
                <span className="admin-badge">👑 Admin Access</span>
              )}
              {hasAccess && user?.role !== 'admin' && (
                <span className="enrolled-badge">✅ Enrolled</span>
              )}
            </div>
            <div className="col-md-4 text-end">
              <button
                onClick={() => navigate(`/premium-batch/${batchId}`)}
                className="btn btn-outline-primary"
              >
                ← Back to Batch Details
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12 quizzes-container">
            {/* Error Message */}
            {error && (
              <div className="alert alert-danger text-center" role="alert">
                {error}
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <Spinner />
            ) : quizzes && quizzes.length > 0 ? (
              <>
                <div className="container my-3 flex-grow-1">
                  <div className="row">
                    {quizzes.map((quiz) => (
                      <React.Fragment key={quiz._id}>
                        <div className="col-md-4">
                          <div className="my-3">
                            <div className="quiz-card-wrapper">
                              <Link
                                to={`/get-premium-quiz/${batchId}/${quiz._id}`}
                                className="card-link"
                              >
                                <div className="card quiz-card">
                                  <img
                                    src={quiz.imageUrl || EnglishVocabImg}
                                    className="card-img-top"
                                    alt={quiz.subject}
                                  />
                                  <div className="card-body">
                                    <h5 className="card-title">
                                      📅 {formatDate(quiz.date)}
                                    </h5>
                                    <h5 className="card-subject">{quiz.subject}</h5>
                                    <p className="card-text">
                                      📝 {quiz.questionCount || quiz.questions?.length || 0} Questions
                                    </p>
                                    <div className="quiz-card-footer">
                                      <span className="start-quiz-btn">
                                        Start Quiz →
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </Link>
                              
                              {/* ✅ Admin Delete Button */}
                              {user?.role === 'admin' && (
                                <button
                                  className="btn-delete-quiz"
                                  onClick={(e) => handleDeleteQuiz(quiz._id, quiz.subject, e)}
                                  disabled={deletingQuizId === quiz._id}
                                  title="Delete Quiz"
                                >
                                  {deletingQuizId === quiz._id ? (
                                    <span className="spinner-small"></span>
                                  ) : (
                                    '🗑️'
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center my-4">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className="btn btn-primary me-3"
                    >
                      ← Previous
                    </button>
                    <span className="align-self-center pagination-info">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className="btn btn-primary ms-3"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="no-quizzes-container">
                <div className="no-quizzes-icon">📝</div>
                <h3>No Quizzes Available Yet</h3>
                <p>New quizzes will be added soon. Stay tuned!</p>
                {user?.role === 'admin' && (
                  <button
                    onClick={() => navigate('/createquiz')}
                    className="btn btn-warning mt-3"
                  >
                    ➕ Create Quiz
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}